// Avviso prima di salvare un turno: sul servizio fiduciario ci sono già altri collaboratori
// negli stessi orari. Non blocca: "Salva comunque" prosegue, "Annulla" torna al form.
// Se il controllo non risponde si salva come prima, senza avviso.
import React, { useCallback, useRef, useState } from 'react';
import { AlertTriangle } from 'lucide-react';
import { Modal } from './Modal';
import { ServizioFiduciarioService } from '../services/apiService';
import { Compresenza, SlotTurno } from '../types';

const dataIt = (iso?: string) => {
    if (!iso) return '';
    const [y, m, d] = String(iso).slice(0, 10).split('-');
    return `${d}/${m}/${y}`;
};

export function useAvvisoCompresenze() {
    const [lista, setLista] = useState<Compresenza[] | null>(null);
    const risolvi = useRef<((ok: boolean) => void) | null>(null);

    /** true = si può salvare (nessuna compresenza o confermato), false = annullato. */
    const conferma = useCallback(async (
        servizioId: number, collaboratoreId: number | null | undefined, slots: SlotTurno[], escludiTurnoId?: number,
    ): Promise<boolean> => {
        let trovate: Compresenza[] = [];
        try {
            trovate = await ServizioFiduciarioService.compresenze(servizioId, {
                collaboratoreId: collaboratoreId ?? null, escludiTurnoId: escludiTurnoId ?? null, slots,
            });
        } catch {
            return true;
        }
        if (!trovate.length) return true;
        setLista(trovate);
        return new Promise<boolean>(res => { risolvi.current = res; });
    }, []);

    const chiudi = (ok: boolean) => {
        setLista(null);
        const r = risolvi.current;
        risolvi.current = null;
        if (r) r(ok);
    };

    // Contenitore z-60: l'avviso resta sopra le altre modali (z-50) da cui parte il salvataggio.
    const modale = lista ? (
        <div className="relative z-[60]">
            <Modal title="Servizio già coperto" onClose={() => chiudi(false)}>
                <div className="flex items-start gap-3 mb-4">
                    <AlertTriangle className="w-5 h-5 text-amber-500 shrink-0 mt-0.5" />
                    <p className="text-sm text-gray-700">
                        Su questo servizio fiduciario {lista.length === 1 ? 'è già presente' : 'sono già presenti'} negli stessi orari:
                    </p>
                </div>
                <ul className="space-y-2 mb-5 max-h-64 overflow-y-auto">
                    {lista.map((c, i) => (
                        <li key={`${c.turnoId}-${c.richiestaData}-${i}`} className="text-sm rounded-lg border border-amber-200 bg-amber-50 px-3 py-2">
                            <span className="font-semibold text-gray-800">{c.collaboratore || 'Collaboratore'}</span>
                            <span className="text-gray-700"> dalle {c.oraInizio} alle {c.oraFine}</span>
                            <span className="text-gray-500"> ({dataIt(c.data)}{c.notturno ? ', fino al giorno dopo' : ''})</span>
                        </li>
                    ))}
                </ul>
                <div className="flex justify-end gap-2">
                    <button type="button" onClick={() => chiudi(false)}
                            className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 text-sm font-semibold hover:bg-gray-50">
                        Annulla
                    </button>
                    <button type="button" onClick={() => chiudi(true)}
                            className="px-4 py-2 rounded-lg bg-emerald-600 text-white text-sm font-semibold hover:bg-emerald-700">
                        Salva comunque
                    </button>
                </div>
            </Modal>
        </div>
    ) : null;

    return { conferma, modale };
}
